import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ProteinCategory, DietType } from '../types/recipe';
import type { MeasurementSystem } from '../utils/unitSystem';
import type { TasteAxes, Axis } from '../services/tasteAxes';
import { flipAxis } from '../services/tasteAxes';
import {
  DEFAULT_DAY_PREFERENCE, DEFAULT_WEEKLY_BALANCE, DEFAULT_MEAL_SCHEDULE,
  type MealDietType, type DayPreference, type WeeklyBalance, type MealFrequencyPreferences, type MealSchedulePreferences,
} from './mealSchedule';
import type { AllergenKey } from '../data/allergens';

export { DEFAULT_DAY_PREFERENCE, DEFAULT_WEEKLY_BALANCE, DEFAULT_MEAL_SCHEDULE };
export type { MealDietType, DayPreference, WeeklyBalance, MealFrequencyPreferences, MealSchedulePreferences };

// Rejection reasons
export type RejectionReason =
  | 'dislike_protein'
  | 'too_time_consuming'
  | 'too_complicated'
  | 'made_recently'
  | 'not_interested'
  | 'wrong_meal_type'
  | 'missing_ingredients'
  | 'other';

// Rejection history entry
export interface RejectedRecipe {
  recipeId: string | number;
  recipeName: string;
  proteinCategory: ProteinCategory;
  reason?: RejectionReason;
  timestamp: string;
}

// Acceptance history entry
export interface AcceptedRecipe {
  recipeId: string | number;
  recipeName: string;
  proteinCategory: ProteinCategory;
  cuisine?: string;
  cookTime: number;
  timestamp: string;
}

// User preferences state
export interface UserPreferencesState {
  // Explicit preferences (from feedback)
  dislikedProteins: ProteinCategory[];
  dislikedIngredients: string[];
  maxCookTime: number | null; // null = no limit
  dietaryRestrictions: DietType[];
  otherDietaryNotes: string;
  mealFrequencyPreferences: MealFrequencyPreferences;

  // Implicit preferences (learned from behavior)
  proteinFrequency: Record<ProteinCategory, number>;
  cuisineFrequency: Record<string, number>;
  avgCookTime: number;
  totalPlannedMeals: number;

  // History
  rejectedRecipes: RejectedRecipe[];
  acceptedRecipes: AcceptedRecipe[];

  // Settings
  enableLearning: boolean; // Allow disabling preference learning
  defaultFamilySize: number; // Default servings when adding meals (1-12)
  dailyDigestOptIn: boolean; // Receive the nightly "Paprika Daily" digest email (default on)

  // Meal schedule preferences
  mealSchedule: MealSchedulePreferences;

  // First-use education banners
  hasSeenMyTopRatedBanner: boolean;

  // Dismissed fallback meal prompts
  dismissedFallbackMealIds: string[];

  /**
   * Metric/imperial choice for recipe display.
   *
   * `null` means "follow the recipe" — each recipe opens in the system it was
   * written in, so nothing is ever silently converted before the user asks.
   * Once the user flips the toggle the choice sticks across recipes.
   */
  measurementSystem: MeasurementSystem | null;

  // Kitchen staples the user does NOT want assumed (household-scoped, shared)
  excludedStaples: string[]; // StapleGroup.key values

  /**
   * The three-axis taste profile from the onboarding duels (base, time, heat).
   * `null` for anyone who never took the quiz: the generators then behave
   * exactly as before the profile existed.
   */
  tasteAxes: TasteAxes | null;

  /** Allergens confirmed on the safety screen — a hard filter everywhere. */
  allergens: AllergenKey[];
  /** Free-text allergies ("kiwi"), matched as whole words. */
  customAllergens: string[];
}

const initialState: UserPreferencesState = {
  dislikedProteins: [],
  dislikedIngredients: [],
  maxCookTime: null,
  dietaryRestrictions: [],
  otherDietaryNotes: '',
  mealFrequencyPreferences: {},

  proteinFrequency: {
    [ProteinCategory.POULTRY]: 0,
    [ProteinCategory.RED_MEAT]: 0,
    [ProteinCategory.SEAFOOD]: 0,
    [ProteinCategory.PLANT]: 0,
    [ProteinCategory.DAIRY_EGGS]: 0,
    [ProteinCategory.VEGETARIAN]: 0,
  },
  cuisineFrequency: {},
  avgCookTime: 0,
  totalPlannedMeals: 0,

  rejectedRecipes: [],
  acceptedRecipes: [],

  enableLearning: true,
  defaultFamilySize: 4,
  dailyDigestOptIn: true,

  mealSchedule: DEFAULT_MEAL_SCHEDULE,

  hasSeenMyTopRatedBanner: false,

  dismissedFallbackMealIds: [],

  measurementSystem: null,

  excludedStaples: [],

  tasteAxes: null,
  allergens: [],
  customAllergens: [],
};

const userPreferencesSlice = createSlice({
  name: 'userPreferences',
  initialState,
  reducers: {
    // Track recipe rejection
    recordRejection: (state, action: PayloadAction<RejectedRecipe>) => {
      if (!state.enableLearning) return;

      const rejection = action.payload;

      // Add to history (keep last 100)
      state.rejectedRecipes.unshift(rejection);
      if (state.rejectedRecipes.length > 100) {
        state.rejectedRecipes = state.rejectedRecipes.slice(0, 100);
      }

      // Apply soft penalties based on reason
      if (rejection.reason === 'dislike_protein') {
        // Soft penalty: don't completely block, just reduce preference
        const currentFreq = state.proteinFrequency[rejection.proteinCategory];
        state.proteinFrequency[rejection.proteinCategory] = Math.max(0, currentFreq - 1);
      }

      if (rejection.reason === 'too_time_consuming') {
        // Learn user's time threshold (gradually reduce max acceptable time)
        const currentMax = state.maxCookTime || 999;
        // Don't set it too low - maintain reasonable minimum
        state.maxCookTime = Math.max(15, Math.min(currentMax, rejection.proteinCategory as any)); // This needs recipe cook time
      }

      console.log(`📝 Recorded rejection: ${rejection.recipeName} (${rejection.reason || 'no reason'})`);
    },

    // Track recipe acceptance
    recordAcceptance: (state, action: PayloadAction<AcceptedRecipe>) => {
      if (!state.enableLearning) return;

      const acceptance = action.payload;

      // Add to history (keep last 100)
      state.acceptedRecipes.unshift(acceptance);
      if (state.acceptedRecipes.length > 100) {
        state.acceptedRecipes = state.acceptedRecipes.slice(0, 100);
      }

      // Increase protein frequency
      state.proteinFrequency[acceptance.proteinCategory]++;

      // Increase cuisine frequency
      if (acceptance.cuisine) {
        state.cuisineFrequency[acceptance.cuisine] =
          (state.cuisineFrequency[acceptance.cuisine] || 0) + 1;
      }

      // Update average cook time (running average)
      state.totalPlannedMeals++;
      state.avgCookTime =
        (state.avgCookTime * (state.totalPlannedMeals - 1) + acceptance.cookTime) /
        state.totalPlannedMeals;

      console.log(`✅ Recorded acceptance: ${acceptance.recipeName}`);
      console.log(`   Protein frequencies:`, state.proteinFrequency);
      console.log(`   Avg cook time: ${Math.round(state.avgCookTime)}min`);
    },

    // Explicitly dislike a protein category
    addDislikedProtein: (state, action: PayloadAction<ProteinCategory>) => {
      if (!state.dislikedProteins.includes(action.payload)) {
        state.dislikedProteins.push(action.payload);
        console.log(`🚫 Added disliked protein: ${action.payload}`);
      }
    },

    // Remove disliked protein
    removeDislikedProtein: (state, action: PayloadAction<ProteinCategory>) => {
      state.dislikedProteins = state.dislikedProteins.filter(p => p !== action.payload);
      console.log(`✅ Removed disliked protein: ${action.payload}`);
    },

    // Add disliked ingredient
    addDislikedIngredient: (state, action: PayloadAction<string>) => {
      const ingredient = action.payload.toLowerCase();
      if (!state.dislikedIngredients.includes(ingredient)) {
        state.dislikedIngredients.push(ingredient);
        console.log(`🚫 Added disliked ingredient: ${ingredient}`);
      }
    },

    // Remove disliked ingredient
    removeDislikedIngredient: (state, action: PayloadAction<string>) => {
      const ingredient = action.payload.toLowerCase();
      state.dislikedIngredients = state.dislikedIngredients.filter(i => i !== ingredient);
      console.log(`✅ Removed disliked ingredient: ${ingredient}`);
    },

    // Set max cook time
    setMaxCookTime: (state, action: PayloadAction<number | null>) => {
      state.maxCookTime = action.payload;
      console.log(`⏱️ Set max cook time: ${action.payload || 'unlimited'}min`);
    },

    setDietaryRestrictions: (state, action: PayloadAction<DietType[]>) => {
      state.dietaryRestrictions = action.payload;
    },

    setOtherDietaryNotes: (state, action: PayloadAction<string>) => {
      state.otherDietaryNotes = action.payload;
    },

    setMealFrequencyPreferences: (state, action: PayloadAction<MealFrequencyPreferences>) => {
      state.mealFrequencyPreferences = action.payload;
    },

    // Toggle learning
    setEnableLearning: (state, action: PayloadAction<boolean>) => {
      state.enableLearning = action.payload;
      console.log(`🧠 Preference learning: ${action.payload ? 'enabled' : 'disabled'}`);
    },

    // Set default family size (clamped to 1-12)
    setDefaultFamilySize: (state, action: PayloadAction<number>) => {
      state.defaultFamilySize = Math.max(1, Math.min(12, action.payload));
    },

    // Toggle the nightly Paprika Daily digest email
    setDailyDigestOptIn: (state, action: PayloadAction<boolean>) => {
      state.dailyDigestOptIn = action.payload;
    },

    /**
     * Pick metric or imperial for recipe display. `null` restores "follow the
     * recipe", which is what the "rétablir" affordance dispatches.
     */
    setMeasurementSystem: (state, action: PayloadAction<MeasurementSystem | null>) => {
      state.measurementSystem = action.payload;
    },

    // Reset all preferences
    resetPreferences: (state) => {
      Object.assign(state, initialState);
      console.log('🔄 Reset all preferences to defaults');
    },

    // Set preference for a specific day of week
    setDayPreference: (state, action: PayloadAction<{ dayOfWeek: number; preference: DayPreference }>) => {
      const { dayOfWeek, preference } = action.payload;
      if (!state.mealSchedule) {
        state.mealSchedule = { ...DEFAULT_MEAL_SCHEDULE };
      }
      state.mealSchedule.days[dayOfWeek] = preference;
    },

    // Set weekly balance constraints
    setWeeklyBalance: (state, action: PayloadAction<WeeklyBalance>) => {
      if (!state.mealSchedule) {
        state.mealSchedule = { ...DEFAULT_MEAL_SCHEDULE };
      }
      state.mealSchedule.weeklyBalance = action.payload;
    },

    // Reset meal schedule to defaults
    resetMealSchedule: (state) => {
      state.mealSchedule = DEFAULT_MEAL_SCHEDULE;
    },

    // Clean up old history (older than 30 days)
    cleanupOldHistory: (state) => {
      const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);

      state.rejectedRecipes = state.rejectedRecipes.filter(
        r => new Date(r.timestamp).getTime() > thirtyDaysAgo
      );

      state.acceptedRecipes = state.acceptedRecipes.filter(
        a => new Date(a.timestamp).getTime() > thirtyDaysAgo
      );

      console.log('🧹 Cleaned up old preference history (>30 days)');
    },

    // Track whether user has seen the My Top Rated banner
    setHasSeenMyTopRatedBanner: (state, action: PayloadAction<boolean>) => {
      state.hasSeenMyTopRatedBanner = action.payload;
    },

    // Dismiss a fallback meal prompt
    dismissFallbackMeal(state, action: PayloadAction<string>) {
      if (!state.dismissedFallbackMealIds.includes(action.payload)) {
        state.dismissedFallbackMealIds.push(action.payload);
      }
    },

    // Exclude a kitchen staple from the "assumed have" list
    excludeStaple: (state, action: PayloadAction<string>) => {
      const key = action.payload;
      if (!state.excludedStaples.includes(key)) {
        state.excludedStaples.push(key);
      }
    },

    // Un-exclude a kitchen staple (restore to "assumed have")
    unexcludeStaple: (state, action: PayloadAction<string>) => {
      const key = action.payload;
      state.excludedStaples = state.excludedStaples.filter(k => k !== key);
    },

    setTasteAxes: (state, action: PayloadAction<TasteAxes | null>) => {
      state.tasteAxes = action.payload;
    },

    flipTasteAxis: (state, action: PayloadAction<Axis>) => {
      if (!state.tasteAxes) return;
      state.tasteAxes = flipAxis(state.tasteAxes, action.payload);
    },

    setAllergens: (state, action: PayloadAction<{ keys: AllergenKey[]; custom: string[] }>) => {
      state.allergens = [...new Set(action.payload.keys)];
      state.customAllergens = [...new Set(action.payload.custom.map((c) => c.trim()).filter(Boolean))];
    },

    // Replace state from cloud (post sign-in or post-pairing). Cloud wins.
    hydratePreferencesFromCloud: (
      _state,
      action: PayloadAction<UserPreferencesState>
    ) => action.payload,
  },
});

/**
 * Pull the active scope's preferences doc and overwrite local state.
 */
export const downloadUserPreferencesFromCloud = createAsyncThunk(
  'userPreferences/downloadFromCloud',
  async (userId: string, { getState, dispatch, rejectWithValue }) => {
    try {
      // Lazy-import so test environments that don't fully mock the Firebase
      // config / react-native Platform module don't blow up when importing
      // this slice.
      const { isFirebaseEnabled } = await import('../services/firebaseConfig');
      if (!isFirebaseEnabled()) {
        throw new Error('Firebase not enabled');
      }
      const { fetchUserPreferencesFromCloud } = await import(
        '../services/userPreferencesSyncService'
      );
      const state = getState() as { auth?: { householdId?: string | null } };
      const householdId = state.auth?.householdId ?? null;
      const cloud = await fetchUserPreferencesFromCloud(userId, householdId);
      if (cloud) {
        dispatch(userPreferencesSlice.actions.hydratePreferencesFromCloud(cloud));
      }
      return { hydrated: !!cloud };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to download user preferences');
    }
  }
);

export const {
  recordRejection,
  recordAcceptance,
  addDislikedProtein,
  removeDislikedProtein,
  addDislikedIngredient,
  removeDislikedIngredient,
  setMaxCookTime,
  setDietaryRestrictions,
  setOtherDietaryNotes,
  setMealFrequencyPreferences,
  setEnableLearning,
  resetPreferences,
  cleanupOldHistory,
  setDayPreference,
  setWeeklyBalance,
  resetMealSchedule,
  setDefaultFamilySize,
  setDailyDigestOptIn,
  setMeasurementSystem,
  setHasSeenMyTopRatedBanner,
  dismissFallbackMeal,
  excludeStaple,
  unexcludeStaple,
  setTasteAxes,
  flipTasteAxis,
  setAllergens,
  hydratePreferencesFromCloud,
} = userPreferencesSlice.actions;

// Selectors
export const selectMealSchedule = (state: { userPreferences: UserPreferencesState }) =>
  state.userPreferences?.mealSchedule || DEFAULT_MEAL_SCHEDULE;

export const selectDayPreference = (dayOfWeek: number) =>
  (state: { userPreferences: UserPreferencesState }): DayPreference =>
    state.userPreferences?.mealSchedule?.days?.[dayOfWeek] || DEFAULT_DAY_PREFERENCE;

export const selectDefaultFamilySize = (state: { userPreferences: UserPreferencesState }) =>
  state.userPreferences?.defaultFamilySize ?? 4;

// Missing field (older persisted state) is treated as opted-in.
export const selectDailyDigestOptIn = (state: { userPreferences: UserPreferencesState }) =>
  state.userPreferences?.dailyDigestOptIn ?? true;

export const selectHasSeenMyTopRatedBanner = (state: { userPreferences: UserPreferencesState }) =>
  state.userPreferences?.hasSeenMyTopRatedBanner ?? false;

export const selectDismissedFallbackMealIds = (state: { userPreferences: UserPreferencesState }) =>
  state.userPreferences?.dismissedFallbackMealIds ?? [];

const EMPTY_ALLERGENS: AllergenKey[] = [];
const EMPTY_CUSTOM: string[] = [];

export const selectTasteAxes = (state: { userPreferences: UserPreferencesState }): TasteAxes | null =>
  state.userPreferences?.tasteAxes ?? null;

export const selectAllergens = (state: { userPreferences: UserPreferencesState }): AllergenKey[] =>
  state.userPreferences?.allergens ?? EMPTY_ALLERGENS;

export const selectCustomAllergens = (state: { userPreferences: UserPreferencesState }): string[] =>
  state.userPreferences?.customAllergens ?? EMPTY_CUSTOM;

export default userPreferencesSlice.reducer;
